import type { ApiFlowsExecutionsResponseType } from "@/types/entities/flows";
import { formatDateTime } from "@/utils/dates";
import { getExecutionBadge } from ".";
import { Badge, Heading, Icon } from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";

const OPERATION_LABELS: Record<string, string> = {
  create: "Creación de registro",
  read: "Lectura de registro",
  update: "Actualización de registro",
  deactivate: "Desactivación de registro",
  link_users: "Vincular usuarios",
  unlink_users: "Desvincular usuarios",
};

interface Props {
  execution: ApiFlowsExecutionsResponseType["data"][number]["node_executions"][number];
}

interface FieldData {
  name: string;
  field_type: string;
  is_required: boolean;
  value: string | boolean;
  timer_config?: unknown;
}

interface ExpedienteData {
  expedient_base_entity_id?: string;
  expedient_register_id?: string;
  expedient_id?: string;
  name?: string;
  fields?: Record<string, FieldData>;
  users?: Array<{
    document?: string;
    email?: string;
    role?: string;
  }>;
}

interface ExecutionInputs {
  operation: string;
  data: ExpedienteData | null;
}

interface ExpedienteOutputs {
  success?: boolean;
  message?: string;
  id?: string;
  data?: Record<string, unknown>;
}

// Helper function to extract inputs
const getExecutionInputs = (
  execution: Props["execution"],
): ExecutionInputs | null => {
  if (!execution) {
    return null;
  }
  if (!execution.inputs || execution.inputs.length === 0) {
    return null;
  }

  const inputsMap = new Map(
    execution.inputs.map((input) => [input.name, input.value]),
  );

  const rawData = inputsMap.get("data");
  let parsedData: ExpedienteData | null = null;

  if (rawData) {
    try {
      parsedData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
    } catch {
      parsedData = null;
    }
  }

  return {
    operation: (inputsMap.get("operation") as string) ?? "",
    data: parsedData,
  };
};

const ExpedienteSimpleContent = ({ execution }: Props) => {
  const inputs = useMemo(() => getExecutionInputs(execution), [execution]);

  const operation = useMemo(
    () => inputs?.operation.toLowerCase() ?? "",
    [inputs?.operation],
  );

  const operationLabel = useMemo(
    () => OPERATION_LABELS[operation] ?? operation,
    [operation],
  );

  const outputs = useMemo(() => {
    if (!execution?.outputs) return null;

    // Try to find success/message outputs
    const successOutput = execution.outputs.find(
      (output) => output.name === "success",
    );
    const messageOutput = execution.outputs.find(
      (output) => output.name === "message",
    );
    const idOutput = execution.outputs.find((output) => output.name === "id");
    const dataOutput = execution.outputs.find(
      (output) => output.name === "data",
    );

    return {
      success: successOutput?.value as boolean | undefined,
      message: messageOutput?.value as string | undefined,
      id: idOutput?.value as string | undefined,
      data: dataOutput?.value as Record<string, unknown> | undefined,
    } as ExpedienteOutputs;
  }, [execution?.outputs]);

  if (!inputs || !execution) {
    return (
      <div className="p-4 border border-error-quiet rounded-lg bg-error-silent flex gap-3 items-start">
        <Icon
          type="fas"
          name="exclamation-triangle"
          className="text-error py-2.5"
          size="sm"
          aria-label="Error"
        />
        <div>
          <p className="font-semibold text-error-loud">
            No se pudo cargar la información
          </p>
          <p className="text-sm text-error mt-1">
            No hay datos de ejecución disponibles para este nodo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p>Estado del nodo: {getExecutionBadge(execution.status)}</p>
      <p>Se completó el: {formatDateTime(execution.ended_at || "")}</p>

      <div className="mt-2 border border-neutral rounded-lg p-4 bg-neutral-object flex flex-col gap-1">
        <p className="capitalize">
          Operación: <span className="font-semibold">{operationLabel}</span>
        </p>

        {inputs.data?.expedient_base_entity_id && (
          <p>
            ID Entidad Base:{" "}
            <span className="font-mono text-sm">
              {inputs.data.expedient_base_entity_id}
            </span>
          </p>
        )}

        {inputs.data?.expedient_register_id && (
          <p>
            ID Registro:{" "}
            <span className="font-mono text-sm">
              {inputs.data.expedient_register_id}
            </span>
          </p>
        )}

        {inputs.data?.name && (
          <p>
            Nombre del registro:{" "}
            <span className="font-semibold">{inputs.data.name}</span>
          </p>
        )}

        {/* Show fields for create/update operations */}
        {(operation === "create" || operation === "update") &&
          inputs.data?.fields && (
            <ExpedienteFieldsList fields={inputs.data.fields} />
          )}

        {/* Show users for link/unlink operations */}
        {(operation === "link_users" || operation === "unlink_users") &&
          inputs.data?.users && (
            <ExpedienteUsersList
              users={inputs.data.users}
              operation={operation}
            />
          )}
      </div>

      {/* Show outputs */}
      {outputs && <ExpedienteOutputsSection outputs={outputs} />}
    </div>
  );
};

// Component for displaying fields
const ExpedienteFieldsList = ({
  fields,
}: {
  fields: Record<string, FieldData>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const fieldEntries = Object.entries(fields);

  if (fieldEntries.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 font-semibold text-body hover:text-heading transition-colors"
      >
        <Icon
          type="fas"
          name={isOpen ? "chevron-down" : "chevron-right"}
          size="xs"
        />
        Campos ({fieldEntries.length})
      </button>
      {isOpen && (
        <div className="mt-2 ml-4 border-l-2 border-neutral-quiet pl-4 space-y-2">
          {fieldEntries.map(([fieldId, field]) => (
            <div key={fieldId} className="p-2 bg-neutral-silent rounded-md">
              <div className="flex items-center gap-2">
                <span className="font-medium">{field.name}</span>
                <Badge size="xs" variant="neutral">
                  {field.field_type}
                </Badge>
                {field.is_required && (
                  <Badge size="xs" variant="danger">
                    Requerido
                  </Badge>
                )}
              </div>
              <p className="text-sm text-body-quiet mt-1">
                Valor:{" "}
                <span className="font-mono">
                  {typeof field.value === "boolean"
                    ? field.value
                      ? "Verdadero"
                      : "Falso"
                    : field.value || "(vacío)"}
                </span>
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Component for displaying users
const ExpedienteUsersList = ({
  users,
  operation,
}: {
  users: Array<{ document?: string; email?: string; role?: string }>;
  operation: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (users.length === 0) {
    return null;
  }

  const actionLabel =
    operation === "link_users" ? "Vinculados" : "Desvinculados";

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 font-semibold text-body hover:text-heading transition-colors"
      >
        <Icon
          type="fas"
          name={isOpen ? "chevron-down" : "chevron-right"}
          size="xs"
        />
        Usuarios {actionLabel} ({users.length})
      </button>
      {isOpen && (
        <div className="mt-2 ml-4 border-l-2 border-neutral-quiet pl-4 space-y-2">
          {users.map((user, index) => (
            <div key={index} className="p-2 bg-neutral-silent rounded-md">
              {user.document && (
                <p className="text-sm">
                  <span className="font-medium">Documento:</span>{" "}
                  <span className="font-mono">{user.document}</span>
                </p>
              )}
              {user.email && (
                <p className="text-sm">
                  <span className="font-medium">Email:</span>{" "}
                  <span className="font-mono">{user.email}</span>
                </p>
              )}
              {user.role && (
                <p className="text-sm">
                  <span className="font-medium">Rol:</span>{" "}
                  <Badge size="xs" variant="info">
                    {user.role}
                  </Badge>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Component for displaying outputs
const ExpedienteOutputsSection = ({
  outputs,
}: {
  outputs: ExpedienteOutputs;
}) => {
  const hasOutputs =
    outputs.success !== undefined ||
    outputs.message ||
    outputs.id ||
    outputs.data;

  if (!hasOutputs) {
    return null;
  }

  return (
    <div className="mt-4">
      <Heading variant="h6" className="mb-2">
        Resultado
      </Heading>
      <div className="border border-neutral rounded-lg p-4 bg-neutral-object flex flex-col gap-1">
        {outputs.success !== undefined && (
          <p>
            Estado:{" "}
            {outputs.success ? (
              <span className="text-success-loud font-semibold">✓ Exitoso</span>
            ) : (
              <span className="text-danger-loud font-semibold">✗ Fallido</span>
            )}
          </p>
        )}
        {outputs.message && (
          <p>
            Mensaje: <span className="font-medium">{outputs.message}</span>
          </p>
        )}
        {outputs.id && (
          <p>
            ID del registro:{" "}
            <span className="font-mono text-sm">{outputs.id}</span>
          </p>
        )}
        {outputs.data && Object.keys(outputs.data).length > 0 && (
          <div className="mt-2">
            <p className="font-semibold mb-1">Datos de salida:</p>
            <pre className="text-xs bg-neutral-silent p-2 rounded overflow-auto max-h-40">
              {JSON.stringify(outputs.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpedienteSimpleContent;
